import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CubeIcon,
  BuildingOfficeIcon,
  UsersIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  SparklesIcon,
  ServerIcon,
  CircleStackIcon,
  TrashIcon,
  CloudArrowUpIcon,
  KeyIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  ArrowsPointingInIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  CodeBracketIcon,
  TableCellsIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { NavItem } from '../../types';

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Models', href: '/models', icon: CubeIcon },
  { name: 'Entities', href: '/entities', icon: BuildingOfficeIcon },
  { name: 'Members', href: '/members', icon: UsersIcon },
  {
    name: 'Match & Merge',
    href: '/match-merge',
    icon: MagnifyingGlassIcon,
    children: [
      { name: 'Duplicate Detection', href: '/match-merge/detect', icon: MagnifyingGlassIcon },
      { name: 'Golden Records', href: '/match-merge/golden-records', icon: SparklesIcon },
      { name: 'Cross-Reference Views', href: '/match-merge/cross-reference', icon: ArrowsPointingInIcon },
    ],
  },
  { name: 'Subscription Views', href: '/subscription-views', icon: ServerIcon },
  { name: 'Data Browser', href: '/browser', icon: ChartBarIcon },
  { name: 'Hierarchies', href: '/hierarchies', icon: Squares2X2Icon },
  { name: 'Business Metrics', href: '/business-metrics', icon: ChartBarIcon },
  {
    name: 'Administration',
    href: '/admin',
    icon: ShieldCheckIcon,
    children: [
      { name: 'Admin Dashboard', href: '/admin', icon: HomeIcon },
      { name: 'User Management', href: '/admin/users', icon: UsersIcon },
      { name: 'Data Quality', href: '/admin/data-quality', icon: CheckCircleIcon },
      { name: 'System Settings', href: '/admin/settings', icon: Cog6ToothIcon },
      { name: 'SQL Integration Guide', href: '/sql-integration-guide', icon: CircleStackIcon },
      { name: 'Backup & Restore', href: '/admin/backup', icon: CloudArrowUpIcon },
      { name: 'GDPR Compliance', href: '/admin/gdpr-compliance', icon: KeyIcon },
      { name: 'Disaster Recovery', href: '/admin/disaster-recovery', icon: LifebuoyIcon },
      { name: 'System Monitoring', href: '/admin/monitoring', icon: CircleStackIcon },
      { name: 'Health Monitoring', href: '/admin/health', icon: HeartIcon },
      { name: 'Workflow Management', href: '/admin/workflow', icon: ClipboardDocumentListIcon },
      { name: 'Stewardship Tasks', href: '/admin/stewardship-tasks', icon: CheckCircleIcon },
      { name: 'Reference Data', href: '/admin/reference-data', icon: TableCellsIcon },
      { name: 'Attribute Metadata', href: '/admin/attribute-metadata', icon: Cog6ToothIcon },
      { name: 'API Explorer', href: '/admin/api-explorer', icon: CodeBracketIcon },
      { name: 'Demo Deployment', href: '/admin/demo-deployment', icon: RocketLaunchIcon },
      { name: 'Orphaned Members', href: '/admin/orphaned-members', icon: TrashIcon },
      { name: 'Database Migration', href: '/admin/database-migration', icon: ServerIcon },
      { name: 'Reports', href: '/admin/reports', icon: ChartBarIcon },
    ],
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const Sidebar: React.FC = () => {
  const location = useLocation();

  return (
    <div className="flex flex-col w-64 bg-white border-r border-gray-200">
      {/* Logo */}
      <div className="flex items-center h-16 px-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <CubeIcon className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="ml-3">
            <h1 className="text-lg font-semibold text-gray-900">MDM App</h1>
            <p className="text-xs text-gray-500">Master Data Management</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1">
        {navigation.map((item) => (
          <div key={item.name}>
            <Link
              to={item.href}
              className={classNames(
                location.pathname === item.href
                  ? 'bg-primary-50 border-primary-500 text-primary-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                'group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-4 transition-colors duration-200'
              )}
            >
              <item.icon
                className={classNames(
                  location.pathname === item.href
                    ? 'text-primary-500'
                    : 'text-gray-400 group-hover:text-gray-500',
                  'mr-3 h-5 w-5 flex-shrink-0'
                )}
                aria-hidden="true"
              />
              {item.name}
            </Link>
            
            {/* Sub-navigation */}
            {item.children && location.pathname.startsWith(item.href) && (
              <div className="ml-8 mt-1 space-y-1">
                {item.children.map((child) => (
                  <Link
                    key={child.name}
                    to={child.href}
                    className={classNames(
                      location.pathname === child.href
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                      'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200'
                    )}
                  >
                    <child.icon
                      className={classNames(
                        location.pathname === child.href
                          ? 'text-primary-500'
                          : 'text-gray-400 group-hover:text-gray-500',
                        'mr-3 h-4 w-4 flex-shrink-0'
                      )}
                      aria-hidden="true"
                    />
                    {child.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* User info */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-gray-700">U</span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">Admin User</p>
            <p className="text-xs text-gray-500">admin@mdm.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;

